/**
 * Flow Matrix data layer.
 *
 * One request per (symbol, expiry window, strike window), normalised into a strike ladder with the
 * call and put side of each metric. Selecting a DTE genuinely narrows the payload now — 1DTE on SPY
 * is ~700KB against 23MB for the whole chain — so the matrix can afford to prefetch the selections
 * a user is about to make instead of blocking on them.
 *
 * Caching is deliberately two-layer: an in-memory map for instant re-renders, and sessionStorage so
 * switching away and back (or a reload) paints from the last good ladder while the network refreshes
 * behind it. A cache entry is never invented — an empty ladder stays empty rather than falling back
 * to a wider window that would quietly answer a different question.
 */

import { getApiUrl } from '../config/api';
import { fetchStableAccumulatedJson } from '../services/stableAccumulatedRequest';
import { computeSpotGammaGexMme } from './gammaCalculations';

export type MatrixSide = 'call' | 'put';

/**
 * Where a ladder comes from. `stable` is the snapshot chain (greeks, OI, the day's volume);
 * `live` is the real-time flow feed, which carries the bid/ask split and trade counts but no greeks.
 */
export type MatrixSource = 'stable' | 'live';

/** Live feed only: the whole session so far, or the latest 5-minute interval. */
export type MatrixLiveWindow = 'session' | '5min';

export interface MatrixReadContext {
  source: MatrixSource;
  liveWindow: MatrixLiveWindow;
  spot: number;
}

export interface MatrixMetricDef {
  id: string;
  label: string;
  labelAr: string;
  /** Row field holding this metric (stable chain). */
  field: string;
  /** Other spellings of the same field, tried in order when `field` is absent. */
  alt?: string[];
  /** Custom reader, used instead of `field` when the value is derived or source-dependent. */
  read?: (row: Record<string, unknown>, ctx: MatrixReadContext) => number;
  /** Sources that carry this metric. */
  sources: MatrixSource[];
  /** Values can be negative and the sign means something (net flow, exposure). */
  signed?: boolean;
  /** Bid-side or ask-side measure: Bid reads red, Ask reads green on every tab. */
  tone?: 'bid' | 'ask';
  /** Short unit shown in the header. */
  unit?: string;
  format: (v: number) => string;
  /** Sum across strikes (volume, OI) vs average (greeks). */
  aggregate: 'sum' | 'avg';
}

const fmtInt = (v: number) => (Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}K` : `${Math.round(v)}`);
const fmtNum = (v: number) => (Math.abs(v) >= 100 ? v.toFixed(0) : Math.abs(v) >= 1 ? v.toFixed(2) : v.toFixed(4));
const fmtMoney = (v: number) => {
  const a = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${Math.round(a / 1e3)}K`;
  return `${sign}$${Math.round(a)}`;
};
const fmtBig = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(v / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return a < 1 ? v.toFixed(4) : v.toFixed(2);
};

/** First finite, non-zero number among the given fields. */
function pick(row: Record<string, unknown>, fields: string[]): number {
  for (const f of fields) {
    const v = row[f];
    if (v == null || v === '' || v === '-') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/,/g, ''));
    if (Number.isFinite(n) && n !== 0) return n;
  }
  return 0;
}

const liveBid = (row: Record<string, unknown>, ctx: MatrixReadContext) =>
  ctx.liveWindow === '5min' ? pick(row, ['Bid Volume']) : pick(row, ['Bid Volume accumelated', 'Bid Volume accumulated']);
const liveAsk = (row: Record<string, unknown>, ctx: MatrixReadContext) =>
  ctx.liveWindow === '5min' ? pick(row, ['Ask Volume']) : pick(row, ['Ask Volume accumelated', 'Ask Volume accumulated']);
const liveBidPrem = (row: Record<string, unknown>, ctx: MatrixReadContext) =>
  ctx.liveWindow === '5min'
    ? pick(row, ['Bid Volume Premium', 'Bid Vol Premium'])
    : pick(row, ['Bid Accumulated Premium', 'Bid Acc Premium']);
const liveAskPrem = (row: Record<string, unknown>, ctx: MatrixReadContext) =>
  ctx.liveWindow === '5min'
    ? pick(row, ['Ask Volume Premium', 'Ask Vol Premium'])
    : pick(row, ['Ask Accumulated Premium', 'Ask Acc Premium']);

export const MATRIX_METRICS: MatrixMetricDef[] = [
  {
    id: 'volume',
    label: 'Volume',
    labelAr: 'حجم التداول',
    field: 'Volume',
    // On the live feed the traded volume is the bid side plus the ask side for the chosen window.
    read: (row, ctx) => (ctx.source === 'live' ? liveBid(row, ctx) + liveAsk(row, ctx) : pick(row, ['Volume'])),
    sources: ['stable', 'live'],
    format: fmtInt,
    aggregate: 'sum',
  },
  { id: 'bid', label: 'Bid volume', labelAr: 'حجم العرض', field: 'Bid Volume', read: liveBid, sources: ['live'], tone: 'bid', format: fmtInt, aggregate: 'sum' },
  { id: 'ask', label: 'Ask volume', labelAr: 'حجم الطلب', field: 'Ask Volume', read: liveAsk, sources: ['live'], tone: 'ask', format: fmtInt, aggregate: 'sum' },
  {
    id: 'net',
    label: 'Net flow (Ask − Bid)',
    labelAr: 'صافي التدفق (طلب − عرض)',
    field: 'Delta ( ask - bid )',
    read: (row, ctx) => liveAsk(row, ctx) - liveBid(row, ctx),
    sources: ['live'],
    signed: true,
    format: fmtInt,
    aggregate: 'sum',
  },
  {
    id: 'net_premium',
    label: 'Net premium (Ask − Bid)',
    labelAr: 'صافي العلاوة (طلب − عرض)',
    field: 'Ask Acc Premium',
    read: (row, ctx) => liveAskPrem(row, ctx) - liveBidPrem(row, ctx),
    sources: ['live'],
    signed: true,
    format: fmtMoney,
    aggregate: 'sum',
  },
  {
    id: 'trades',
    label: 'Trades',
    labelAr: 'عدد الصفقات',
    field: 'Trades',
    read: (row, ctx) =>
      ctx.liveWindow === '5min' ? pick(row, ['Trades', 'Trade Count']) : pick(row, ['Trades accumelated', 'Trades accumulated']),
    sources: ['live'],
    format: fmtInt,
    aggregate: 'sum',
  },
  { id: 'oi', label: 'Open Interest', labelAr: 'العقود المفتوحة', field: 'Open Interest', sources: ['stable', 'live'], format: fmtInt, aggregate: 'sum' },
  {
    id: 'premium',
    label: 'Premium',
    labelAr: 'العلاوة',
    field: 'Total Premium',
    read: (row, ctx) =>
      ctx.source === 'live' ? liveAskPrem(row, ctx) + liveBidPrem(row, ctx) : pick(row, ['Total Premium']),
    sources: ['stable', 'live'],
    format: fmtMoney,
    aggregate: 'sum',
  },
  { id: 'gamma', label: 'Gamma', labelAr: 'جاما', field: 'Gamma', sources: ['stable'], format: fmtNum, aggregate: 'sum' },
  {
    id: 'spot_gamma',
    label: 'Spot Gamma',
    labelAr: 'جاما السعر الحالي',
    field: 'Gamma',
    read: (row, ctx) => computeSpotGammaGexMme(row, 'accumulated', ctx.spot).spotGamma,
    sources: ['stable'],
    signed: true,
    format: fmtBig,
    aggregate: 'sum',
  },
  {
    id: 'gex',
    label: 'GEX',
    labelAr: 'تعرض جاما GEX',
    field: 'Gamma',
    read: (row, ctx) => computeSpotGammaGexMme(row, 'accumulated', ctx.spot).gex,
    sources: ['stable'],
    signed: true,
    format: fmtBig,
    aggregate: 'sum',
  },
  { id: 'delta', label: 'Delta', labelAr: 'دلتا', field: 'Delta', sources: ['stable'], format: fmtNum, aggregate: 'avg' },
  { id: 'vega', label: 'Vega', labelAr: 'فيغا', field: 'Vega', sources: ['stable'], format: fmtNum, aggregate: 'avg' },
  { id: 'theta', label: 'Theta', labelAr: 'ثيتا', field: 'Theta', sources: ['stable'], format: fmtNum, aggregate: 'avg' },
  { id: 'vanna', label: 'Vanna', labelAr: 'فانا', field: 'Vanna', sources: ['stable'], format: fmtNum, aggregate: 'avg' },
  { id: 'vol_oi', label: 'Vol / OI', labelAr: 'الحجم / العقود المفتوحة', field: 'Vol/OI', sources: ['stable'], format: fmtNum, aggregate: 'avg' },
  // The stable chain spells it "Implied Vol"; older payloads used the long form.
  {
    id: 'iv',
    label: 'Implied Vol',
    labelAr: 'التذبذب الضمني',
    field: 'Implied Volatility',
    alt: ['Implied Vol', 'implied_vol'],
    sources: ['stable'],
    format: fmtNum,
    aggregate: 'avg',
  },
];

/** Metrics a source carries, in display order. */
export function metricsForSource(source: MatrixSource): MatrixMetricDef[] {
  return MATRIX_METRICS.filter((m) => m.sources.includes(source));
}

export const MATRIX_METRIC_BY_ID: Record<string, MatrixMetricDef> = MATRIX_METRICS.reduce(
  (acc, m) => {
    acc[m.id] = m;
    return acc;
  },
  {} as Record<string, MatrixMetricDef>,
);

/** Expiry windows the backend understands (0dte/1dte were added server-side for this view). */
export const MATRIX_DTE_OPTIONS = [
  { value: '0dte', label: '0DTE' },
  { value: '1dte', label: '1DTE' },
  { value: '0dte,1dte', label: '0+1' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'all', label: 'All' },
] as const;

export const MATRIX_STRIKE_OPTIONS = [
  { value: '10', label: '±10' },
  { value: '20', label: '±20' },
  { value: '40', label: '±40' },
  { value: 'all', label: 'All' },
] as const;

export interface MatrixStrikeRow {
  strike: number;
  call: Record<string, number>;
  put: Record<string, number>;
  /** Nearest-expiry label seen for this strike, for the drill-down. */
  expiry: string;
}

export interface MatrixLadder {
  symbol: string;
  spot: number;
  rows: MatrixStrikeRow[];
  expiries: string[];
  /** Epoch ms the ladder was fetched; drives the freshness badge. */
  fetchedAt: number;
  /** Absent on ladders cached before sources existed: those are stable. */
  source?: MatrixSource;
  /** Live only: every expiry the feed carried before the window was applied. */
  feedExpiries?: string[];
}

export interface MatrixQuery {
  symbol: string;
  dte: string;
  strikeRange: string;
  /** Omitted = stable, which keeps the original single-pane query (and its cache keys) unchanged. */
  source?: MatrixSource;
  /** Live source only; defaults to the session. */
  liveWindow?: MatrixLiveWindow;
}

export function matrixCacheKey(q: MatrixQuery): string {
  const base = `${q.symbol.toUpperCase()}|${q.dte}|${q.strikeRange}`;
  if ((q.source ?? 'stable') === 'stable') return base;
  return `live|${base}|${q.liveWindow ?? 'session'}`;
}

/** The live feed keys the S&P index chain as SPXW; everything else is its own ticker. */
export function liveFeedSymbol(symbol: string): string {
  const s = symbol.trim().toUpperCase().replace(/^\$/, '').replace(/\.X$/, '');
  return s === 'SPX' ? 'SPXW' : s;
}

/** Same underlying for strike-alignment purposes (SPX and SPXW share strikes). */
export function sameUnderlying(a: string, b: string): boolean {
  return liveFeedSymbol(a) === liveFeedSymbol(b);
}

const SESSION_PREFIX = 'mmecFlowMatrix:';
const memory = new Map<string, MatrixLadder>();
const inflight = new Map<string, Promise<MatrixLadder>>();

function num(v: unknown): number {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
}

function readSession(key: string): MatrixLadder | null {
  try {
    const raw = window.sessionStorage.getItem(SESSION_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && Array.isArray(parsed.rows) ? (parsed as MatrixLadder) : null;
  } catch {
    return null;
  }
}

function writeSession(key: string, ladder: MatrixLadder): void {
  try {
    window.sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(ladder));
  } catch {
    // Quota or private mode: the in-memory copy still serves this session.
  }
}

/** Cached ladder for a query, memory first then sessionStorage. Null when never fetched. */
export function peekLadder(q: MatrixQuery): MatrixLadder | null {
  const key = matrixCacheKey(q);
  const hit = memory.get(key);
  if (hit) return hit;
  const stored = readSession(key);
  if (stored) memory.set(key, stored);
  return stored;
}

/** "YYYY-MM-DD…" (stable) or "MM/DD/YY" (live feed) → "YYYY-MM-DD"; "" when unreadable. */
export function isoExpiry(raw: unknown): string {
  const t = String(raw ?? '').trim();
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    const y = m[3].length <= 2 ? 2000 + Number(m[3]) : Number(m[3]);
    return `${y}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  }
  m = t.match(/^(\d{4})(\d{2})(\d{2})$/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
}

/**
 * The expiries a window keeps, mirroring the server's own rule for the stable chain (UTC days,
 * expired dates dropped, whole days to expiry rounded down) so a live pane and a stable pane set
 * to the same window cover the same expirations.
 */
export function pickExpiries(present: string[], range: string, now: Date = new Date()): string[] {
  const r = (range || '').trim().toLowerCase();
  const today = now.toISOString().slice(0, 10);
  const open = Array.from(new Set(present.filter(Boolean))).sort().filter((e) => e >= today);
  if (!r || r === 'all') return open;
  if (r === 'monthly' || r === 'this-month') return open.filter((e) => e.slice(0, 7) === today.slice(0, 7));
  const days = (e: string) => Math.max(0, Math.floor((Date.parse(`${e}T00:00:00Z`) - now.getTime()) / 86400000));
  if (r === 'weekly' || r === 'this-week') return open.filter((e) => days(e) <= 7);
  const tokens = r.split(',').map((x) => x.trim()).filter(Boolean);
  if (tokens.length && tokens.every((x) => x === '0dte' || x === '1dte')) {
    const wanted = new Set<number>(tokens.map((x) => (x === '0dte' ? 0 : 1)));
    return open.filter((e) => wanted.has(days(e)));
  }
  return open;
}

export interface BuildLadderOptions {
  source?: MatrixSource;
  liveWindow?: MatrixLiveWindow;
}

/** Collapse the raw chain into one row per strike, call side and put side separated. */
export function buildLadder(
  symbol: string,
  rows: Record<string, unknown>[],
  opts: BuildLadderOptions = {},
): MatrixLadder {
  const source: MatrixSource = opts.source ?? 'stable';
  const byStrike = new Map<number, MatrixStrikeRow>();
  const expiries = new Set<string>();
  let spot = 0;
  for (const row of rows) {
    spot = num(row['Underlying Price'] ?? row.underlying_price);
    if (spot) break;
  }
  const ctx: MatrixReadContext = { source, liveWindow: opts.liveWindow ?? 'session', spot };
  const metrics = metricsForSource(source);

  for (const row of rows) {
    const strike = num(row.Strike ?? row.strike);
    if (!strike) continue;
    const side: MatrixSide = String(row['Option Type'] ?? row.option_type ?? '')
      .toUpperCase()
      .startsWith('P')
      ? 'put'
      : 'call';
    const expiry = isoExpiry(row['Expiry Date'] ?? row.expiry_date ?? '');
    if (expiry) expiries.add(expiry);

    let entry = byStrike.get(strike);
    if (!entry) {
      entry = { strike, call: {}, put: {}, expiry };
      byStrike.set(strike, entry);
    } else if (expiry && (!entry.expiry || expiry < entry.expiry)) {
      entry.expiry = expiry;
    }
    const target = entry[side];
    for (const metric of metrics) {
      const v = metric.read ? metric.read(row, ctx) : pick(row, [metric.field, ...(metric.alt ?? [])]);
      if (!v || !Number.isFinite(v)) continue;
      // Multiple expirations can share a strike; sum them so the ladder covers the whole window.
      target[metric.id] = (target[metric.id] ?? 0) + v;
    }
  }

  return {
    symbol: symbol.toUpperCase(),
    spot,
    rows: Array.from(byStrike.values()).sort((a, b) => a.strike - b.strike),
    expiries: Array.from(expiries).sort(),
    fetchedAt: Date.now(),
    source,
  };
}

// ── Live feed ───────────────────────────────────────────────────────────────
//
// The live endpoint answers per underlying and ignores the expiry and strike windows, so one raw
// download serves every live pane on that underlying; the window, strikes and session/5-minute
// choice are applied here. A short reuse window keeps two panes on the same symbol from
// downloading it twice.

const LIVE_RAW_REUSE_MS = 15000;
const liveRaw = new Map<string, { rows: Record<string, unknown>[]; at: number }>();
const liveRawInflight = new Map<string, Promise<Record<string, unknown>[]>>();

async function fetchLiveRows(symbol: string, signal?: AbortSignal): Promise<Record<string, unknown>[]> {
  const feed = liveFeedSymbol(symbol);
  const hit = liveRaw.get(feed);
  if (hit && Date.now() - hit.at < LIVE_RAW_REUSE_MS) return hit.rows;
  const running = liveRawInflight.get(feed);
  if (running) return running;
  const task = (async () => {
    const params = new URLSearchParams({ source: 'rtl_legacy', underlyings: feed });
    const res = await fetch(getApiUrl(`/api/accumulated-data?${params.toString()}`), { signal });
    if (!res.ok) throw new Error(`Live feed HTTP ${res.status}`);
    const json = await res.json();
    const rows = (Array.isArray(json?.data) ? json.data : []) as Record<string, unknown>[];
    liveRaw.set(feed, { rows, at: Date.now() });
    return rows;
  })();
  liveRawInflight.set(feed, task);
  try {
    return await task;
  } finally {
    liveRawInflight.delete(feed);
  }
}

/** Keep the `n` strikes nearest spot (the stable server's strike_range rule); "all" keeps every row. */
function nearestStrikes(rows: Record<string, unknown>[], strikeRange: string, spot: number): Record<string, unknown>[] {
  const n = parseInt(strikeRange, 10);
  if (!Number.isFinite(n) || n <= 0 || !spot) return rows;
  const strikes = Array.from(new Set(rows.map((r) => num(r.Strike ?? r.strike)).filter((k) => k > 0)));
  strikes.sort((a, b) => Math.abs(a - spot) - Math.abs(b - spot) || a - b);
  const keep = new Set(strikes.slice(0, n));
  return rows.filter((r) => keep.has(num(r.Strike ?? r.strike)));
}

function buildLiveLadder(q: MatrixQuery, raw: Record<string, unknown>[]): MatrixLadder {
  const feedExpiries = Array.from(
    new Set(raw.map((r) => isoExpiry(r['Expiry Date'] ?? r.expiry_date)).filter(Boolean)),
  ).sort();
  // "All" on the live feed means everything it carries, including a session that has just
  // expired: before the open the feed still holds yesterday's flow, and that is worth reading.
  const keep = new Set((q.dte || '').toLowerCase() === 'all' ? feedExpiries : pickExpiries(feedExpiries, q.dte));
  const inWindow = raw.filter((r) => keep.has(isoExpiry(r['Expiry Date'] ?? r.expiry_date)));
  let spot = 0;
  for (const r of raw) {
    spot = num(r['Underlying Price'] ?? r.underlying_price);
    if (spot) break;
  }
  const ladder = buildLadder(q.symbol, nearestStrikes(inWindow, q.strikeRange, spot), {
    source: 'live',
    liveWindow: q.liveWindow ?? 'session',
  });
  if (!ladder.spot) ladder.spot = spot;
  ladder.feedExpiries = feedExpiries;
  return ladder;
}

/**
 * Fetch one ladder. Concurrent callers for the same key share a single request, so hovering a
 * chip that is already loading never doubles the work.
 */
export async function fetchLadder(q: MatrixQuery, signal?: AbortSignal): Promise<MatrixLadder> {
  const key = matrixCacheKey(q);
  const running = inflight.get(key);
  if (running) return running;

  const task =
    (q.source ?? 'stable') === 'live'
      ? (async () => {
          const ladder = buildLiveLadder(q, await fetchLiveRows(q.symbol, signal));
          memory.set(key, ladder);
          writeSession(key, ladder);
          return ladder;
        })()
      : (async () => {
          const params = new URLSearchParams({
            source: 'stable',
            underlyings: q.symbol.toUpperCase(),
            strike_range: q.strikeRange,
            expiry_range: q.dte,
            fields: 'lite',
          });
          const url = getApiUrl(`/api/accumulated-data?${params.toString()}`);
          const json = await fetchStableAccumulatedJson(url, signal);
          const rows = (json?.data ?? []) as Record<string, unknown>[];
          const ladder = buildLadder(q.symbol, rows);
          memory.set(key, ladder);
          writeSession(key, ladder);
          return ladder;
        })();

  inflight.set(key, task);
  try {
    return await task;
  } finally {
    inflight.delete(key);
  }
}

/**
 * Warm a query without caring about the result — used on hover and for the neighbouring DTE
 * windows, so the common next click is already in memory. Never throws.
 */
export function prefetchLadder(q: MatrixQuery): void {
  const key = matrixCacheKey(q);
  if (memory.has(key) || inflight.has(key)) return;
  fetchLadder(q).catch(() => {
    /* prefetch is best-effort by definition */
  });
}

/** Largest absolute value of a metric across the ladder, for heat scaling. */
export function metricPeak(ladder: MatrixLadder, metricId: string): number {
  let peak = 0;
  for (const row of ladder.rows) {
    peak = Math.max(peak, Math.abs(row.call[metricId] ?? 0), Math.abs(row.put[metricId] ?? 0));
  }
  return peak;
}

export interface MatrixTotals {
  call: number;
  put: number;
  ratio: number | null;
}

/** Call/put totals for the selected metric, respecting sum vs average semantics. */
export function metricTotals(ladder: MatrixLadder, metricId: string): MatrixTotals {
  const def = MATRIX_METRIC_BY_ID[metricId];
  let callSum = 0;
  let putSum = 0;
  let callN = 0;
  let putN = 0;
  for (const row of ladder.rows) {
    const c = row.call[metricId];
    const p = row.put[metricId];
    if (c) {
      callSum += c;
      callN += 1;
    }
    if (p) {
      putSum += p;
      putN += 1;
    }
  }
  const call = def?.aggregate === 'avg' && callN ? callSum / callN : callSum;
  const put = def?.aggregate === 'avg' && putN ? putSum / putN : putSum;
  return { call, put, ratio: call ? put / call : null };
}

/** Strike nearest the spot, used to anchor the initial scroll position. */
export function atmIndex(ladder: MatrixLadder): number {
  if (!ladder.spot || !ladder.rows.length) return -1;
  let best = 0;
  let bestDist = Infinity;
  ladder.rows.forEach((row, i) => {
    const d = Math.abs(row.strike - ladder.spot);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  });
  return best;
}
